import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Autocomplete,
  Card,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import Grid from "@mui/material/Grid2";
import { Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import SWR_KEYS from "@/constants/swr-keys";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import useDialog from "@/hooks/use-dialog";
import useMutate from "@/hooks/use-mutate";
import useUpdateWorkflow from "@/hooks/requests/wokflow/use-update-workflow";
import { IWorkflow, IWorkflowInput } from "@/interfaces/workflow.interface";
import { IWorkflowStep } from "@/interfaces/workflow-step.interface";
import { WorkflowEditDialogMessages } from "@/messages/workflow/workflow-edit-dialog.messages";
import workflowService from "@/services/workflow.service";
import { IWorkflowKeyWordRule } from "@/types/workflow-keywords.types";
import {
  finalizeWorkflowKeyWordRules,
  serializeWorkflowKeyWords,
  validateWorkflowKeyWordRules,
} from "@/utils/workflow-keywords";

import OrderReportLayoutPreview from "./components/OrderReportLayoutPreview";
import ProductSearchAssertivenessModal from "./components/ProductSearchAssertivenessModal";
import WorkflowKeyWordsSection from "./components/WorkflowKeyWordsSection";
import { buildWorkflowSettingsInitialData, normalizeWorkflowSettingsData } from "./form-data";
import WhatsAppProductDisplayPreview from "./WhatsAppProductDisplayPreview";

type WorkflowSettingsTabProps = {
  messages: WorkflowEditDialogMessages;
  workflow: IWorkflow | IWorkflowInput | null;
};

type WorkflowFormValues = IWorkflowInput & {
  enableExpiration?: boolean;
  ConversationTimeout?: number | string | null;
  ExpirationStepName?: string | null;
  enableReactivation?: boolean;
  reactivationTimeout?: number | string | null;
  reactivationMessage?: string | null;
  reactivationMaxMessages?: number | string | null;
  reactivationInterval?: number | string | null;
  productSearchAssertiveness?: number | string | null;
  workflowKeyWordsRules?: IWorkflowKeyWordRule[];
  workflowKeyWordDrafts?: Record<number, string>;
};

export default function WorkflowSettingsTab({ messages, workflow }: WorkflowSettingsTabProps) {
  const { lang } = useLanguage();
  const router = useNavigate();
  const { update, loadingIcon, isLoading } = useUpdateWorkflow();
  const { isVisible: isAssertivenessModalOpen, handleOpen: openAssertivenessModal, handleClose: closeAssertivenessModal } =
    useDialog();
  const { mutate } = useMutate();
  const customerId = useGetCustomerId();
  const [supportsCarousel, setSupportsCarousel] = useState<boolean | undefined>(undefined);
  const originalReactivationTimeoutRef = useRef<number | null>(null);
  const originalExpirationTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const fetchSupport = async () => {
      if (!customerId) {
        setSupportsCarousel(undefined);
        return;
      }

      const parsedCustomerId = Number(customerId);
      if (!Number.isFinite(parsedCustomerId) || parsedCustomerId <= 0) {
        setSupportsCarousel(false);
        return;
      }

      try {
        const support = await workflowService.getCustomerSupportsCarousel(parsedCustomerId);
        setSupportsCarousel(Boolean(support));
      } catch {
        setSupportsCarousel(false);
      }
    };

    void fetchSupport();
  }, [customerId]);

  if (originalReactivationTimeoutRef.current === null && workflow) {
    const workflowRecord = workflow as Record<string, unknown>;
    const timeout = (workflowRecord?.reactivationTimeout ?? workflowRecord?.ReactivationTimeout) as number | undefined;
    if (typeof timeout === "number" && timeout > 0) {
      originalReactivationTimeoutRef.current = timeout;
    }
  }

  if (originalExpirationTimeoutRef.current === null && workflow) {
    const workflowRecord = workflow as Record<string, unknown>;
    const timeout = (workflowRecord?.ConversationTimeout ?? workflowRecord?.conversationTimeout) as number | undefined;
    if (typeof timeout === "number") {
      originalExpirationTimeoutRef.current = timeout;
    }
  }

  const handleUpdate = async (formData: WorkflowFormValues) => {
    if (!workflow) return;

    const _id = Number(workflow.id) || 0;
    const successMessage = messages.workflowUpdateSuccess;
    const errorMessage = messages.workflowUpdateError;

    const onSuccess = () => {
      mutate(SWR_KEYS.getWorkflowById);
      mutate(SWR_KEYS.getWorkflows);
      router(ROUTE_URLS.workflow(lang));
    };

    const sanitized = normalizeWorkflowSettingsData(formData, workflow);
    if (supportsCarousel === false) {
      sanitized.productDisplayType = "LIST";
    }
    await update({ _id, formData: sanitized, successMessage, errorMessage, onSuccess });
  };

  const validate = (values: WorkflowFormValues) => {
    const errors: Record<string, string> = {};
    const isExpirationEnabled = Boolean(values.enableExpiration);

    if (isExpirationEnabled) {
      const timeout = values.ConversationTimeout;
      let timeoutNum: number | null = null;
      if (timeout !== undefined && timeout !== null) {
        if (typeof timeout === "string") {
          timeoutNum = timeout !== "" ? Number(timeout) : null;
        } else if (typeof timeout === "number") {
          timeoutNum = timeout;
        }
      }

      const isInvalidExpirationTimeout =
        timeoutNum === null ||
        !Number.isFinite(timeoutNum) ||
        timeoutNum < 0 ||
        (timeoutNum > 0 && timeoutNum < 5);

      if (isInvalidExpirationTimeout) {
        errors.ConversationTimeout = "O tempo de expiração deve ser no mínimo 5 minutos.";
      }

      const stepName = values.ExpirationStepName;
      if (!stepName || (typeof stepName === "string" && stepName.trim().length === 0)) {
        errors.ExpirationStepName = messages.errorRequiredExpirationStepName;
      }
    }

    const isReactivationEnabled = Boolean(values.enableReactivation);

    if (isReactivationEnabled) {
      const timeout = values.reactivationTimeout;
      let timeoutNum: number | null = null;
      if (timeout !== undefined && timeout !== null) {
        if (typeof timeout === "string") {
          timeoutNum = timeout !== "" ? Number(timeout) : null;
        } else if (typeof timeout === "number") {
          timeoutNum = timeout;
        }
      }

      if (timeoutNum === null || !Number.isFinite(timeoutNum) || timeoutNum <= 0) {
        errors.reactivationTimeout = "Tempo de inatividade deve ser maior que 0 quando a reativação está habilitada.";
      }

      const message = values.reactivationMessage;
      if (!message || (typeof message === "string" && message.trim().length === 0)) {
        errors.reactivationMessage = messages.errorRequiredReactivationMessage;
      } else if (typeof message === "string" && message.length > 1000) {
        errors.reactivationMessage = "Mensagem de reativação deve ter no máximo 1000 caracteres.";
      }

      const maxMessages = values.reactivationMaxMessages;
      if (maxMessages !== undefined && maxMessages !== null) {
        let maxMessagesNum: number | null = null;
        if (typeof maxMessages === "string") {
          maxMessagesNum = maxMessages !== "" ? Number(maxMessages) : null;
        } else if (typeof maxMessages === "number") {
          maxMessagesNum = maxMessages;
        }
        if (maxMessagesNum !== null && (!Number.isFinite(maxMessagesNum) || maxMessagesNum < 0)) {
          errors.reactivationMaxMessages = "Máximo de mensagens deve ser maior ou igual a 0.";
        }
      }

      const interval = values.reactivationInterval;
      if (interval !== undefined && interval !== null) {
        let intervalNum: number | null = null;
        if (typeof interval === "string") {
          intervalNum = interval !== "" ? Number(interval) : null;
        } else if (typeof interval === "number") {
          intervalNum = interval;
        }
        if (intervalNum !== null && (!Number.isFinite(intervalNum) || intervalNum < 0)) {
          errors.reactivationInterval = "Intervalo deve ser maior ou igual a 0.";
        }
      }
    }

    const assertiveness = values.productSearchAssertiveness;
    if (assertiveness !== undefined && assertiveness !== null && String(assertiveness) !== "") {
      const assertivenessNum = Number(assertiveness);
      if (!Number.isFinite(assertivenessNum) || assertivenessNum < 0 || assertivenessNum > 100) {
        errors.productSearchAssertiveness = messages.errorInvalidProductSearchAssertiveness;
      }
    }

    const rules = (values as WorkflowFormValues).workflowKeyWordsRules || [];
    const drafts = (values as WorkflowFormValues).workflowKeyWordDrafts || {};

    const incompleteValidation = validateWorkflowKeyWordRules(rules, drafts);
    if (!incompleteValidation.isValid) {
      errors.workflowKeyWords = messages.errorWorkflowKeyWordsIncompleteRule;
    } else {
      const workflowKeyWordsRules = finalizeWorkflowKeyWordRules(rules, drafts);
      try {
        serializeWorkflowKeyWords(workflowKeyWordsRules);
      } catch {
        errors.workflowKeyWords = messages.errorWorkflowKeyWordsTooLong;
      }
    }

    return errors;
  };

  return (
    <>
    <Formik
      initialValues={buildWorkflowSettingsInitialData(workflow)}
      validateOnChange={false}
      validate={validate}
      onSubmit={handleUpdate}
    >
      {({ values, errors, handleChange, submitForm, setFieldValue }) => {
        const formValues = values as WorkflowFormValues;
        const reactivationMessage = formValues.reactivationMessage || "";
        const messageLength = typeof reactivationMessage === "string" ? reactivationMessage.length : 0;

        const selectedDisplayType = String(formValues.productDisplayType || "LIST").toUpperCase();
        const canUseCarousel = supportsCarousel === true;
        const shouldForceList = supportsCarousel === false && selectedDisplayType === "CAROUSEL";
        const previewMode =
          shouldForceList || selectedDisplayType !== "CAROUSEL" ? "LIST" : "CAROUSEL";

        return (
          <MDBox my={4}>
            <Grid size={12}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="bold" mb={2}>
                    {messages.productsSectionTitle}
                  </MDTypography>
                  <Grid container spacing={3} alignItems="flex-start">
                    <Grid size={{ xs: 12, md: 5 }}>
                      <MDBox mb={1} display="flex" alignItems="center" flexWrap="nowrap">
                        <MDTypography variant="h6" fontWeight="regular" sx={{ whiteSpace: "nowrap" }}>
                          {messages.productSearchAssertivenessLabel}
                        </MDTypography>
                        <MDButton
                          variant="outlined"
                          color="secondary"
                          size="small"
                          circular
                          iconOnly
                          onClick={openAssertivenessModal}
                          sx={{ ml: 1, flexShrink: 0 }}
                        >
                          <QuestionMarkIcon fontSize="large" />
                        </MDButton>
                      </MDBox>
                      <TextField
                        variant="standard"
                        name="productSearchAssertiveness"
                        value={formValues.productSearchAssertiveness ?? ""}
                        onChange={(e) => {
                          const nextValue = e.target.value.replace(/[^0-9]/g, "");
                          setFieldValue("productSearchAssertiveness", nextValue);
                        }}
                        type="text"
                        inputProps={{
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          min: 0,
                          max: 100,
                        }}
                        fullWidth
                        error={!!errors.productSearchAssertiveness}
                        helperText={errors.productSearchAssertiveness}
                        placeholder="50"
                      />
                      {!errors.productSearchAssertiveness && (
                        <FormHelperText
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 0.5,
                            mt: 0.5,
                            color: "text.secondary",
                          }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 16, mt: 0.25 }} />
                          <span style={{ whiteSpace: "pre-line" }}>
                            {messages.productSearchAssertivenessShortHint}
                          </span>
                        </FormHelperText>
                      )}
                      <TextField
                        select
                        variant="standard"
                        name="productDisplayType"
                        label={messages.productDisplayTypeLabel}
                        sx={{ mt: 3, "& .MuiSelect-icon": { display: "inline-block", right: 0 } }}
                        slotProps={{ select: { IconComponent: KeyboardArrowDownIcon } }}
                        value={shouldForceList ? "LIST" : selectedDisplayType}
                        onChange={(e) => {
                          const nextValue =
                            String(e.target.value).toUpperCase() === "CAROUSEL" ? "CAROUSEL" : "LIST";
                          const valueToSave =
                            !canUseCarousel && nextValue === "CAROUSEL" ? "LIST" : nextValue;
                          setFieldValue("productDisplayType", valueToSave);
                        }}
                        fullWidth
                      >
                        <MenuItem value="LIST">{messages.productDisplayTypeListOption}</MenuItem>
                        <MenuItem value="CAROUSEL" disabled={!canUseCarousel}>
                          {messages.productDisplayTypeCarouselOption}
                        </MenuItem>
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 7 }}>
                      <WhatsAppProductDisplayPreview
                        mode={previewMode}
                        title={messages.productDisplayPreviewTitle}
                        disclaimer={messages.productDisplayPreviewDisclaimer}
                        virtualAssistantLabel={messages.productDisplayPreviewVirtualAssistant}
                        productNamePrefix={messages.productDisplayPreviewProductNamePrefix}
                        detailsHeader={messages.productDisplayPreviewDetailsHeader}
                        imagePlaceholder={messages.productDisplayPreviewImagePlaceholder}
                        productButtonPrefix={messages.productDisplayPreviewProductButtonPrefix}
                      />
                    </Grid>
                  </Grid>
                </MDBox>
              </Card>
              <Card sx={{ mt: 3 }}>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="bold" mb={2}>
                    {messages.ordersSectionTitle}
                  </MDTypography>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 5 }}>
                      <TextField
                        select
                        variant="standard"
                        name="orderReportLayout"
                        label={messages.orderReportLayoutLabel}
                        sx={{ "& .MuiSelect-icon": { display: "inline-block", right: 0 } }}
                        slotProps={{ select: { IconComponent: KeyboardArrowDownIcon } }}
                        value={
                          String(formValues.orderReportLayout ?? "").toUpperCase() === "R" ? "R" : "A"
                        }
                        onChange={(e) =>
                          setFieldValue("orderReportLayout", e.target.value === "R" ? "R" : "A")
                        }
                        fullWidth
                      >
                        <MenuItem value="A">{messages.orderReportLayoutOrderOption}</MenuItem>
                        <MenuItem value="R">{messages.orderReportLayoutReceiptOption}</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 7 }}>
                      <OrderReportLayoutPreview
                        layout={
                          String(formValues.orderReportLayout ?? "").toUpperCase() === "R" ? "R" : "A"
                        }
                      />
                    </Grid>
                  </Grid>
                </MDBox>
              </Card>
            </Grid>

            {/* Expiração */}
            <Grid size={12} mt={2}>
              <Card>
                <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="h6" fontWeight={"bold"}>
                    {messages.expirationSectionTitle}
                  </MDTypography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(formValues.enableExpiration)}
                        onChange={(_, checked) => {
                          if (checked) {
                            const curr = formValues.ConversationTimeout;
                            const originalValue = originalExpirationTimeoutRef.current;

                            if (originalValue !== null && originalValue >= 0) {
                              const normalizedOriginalValue =
                                originalValue === 0 ? 0 : Math.max(5, originalValue);
                              setFieldValue("ConversationTimeout", normalizedOriginalValue);
                            } else if (
                              curr === undefined ||
                              curr === null ||
                              (typeof curr === "string" && curr === "") ||
                              (typeof curr === "number" && curr < 0)
                            ) {
                              setFieldValue("ConversationTimeout", 180);
                            } else {
                              const currNum = Number(curr);
                              if (
                                !Number.isFinite(currNum) ||
                                currNum < 0 ||
                                (currNum > 0 && currNum < 5)
                              ) {
                                setFieldValue("ConversationTimeout", 180);
                              }
                            }
                            setFieldValue("enableExpiration", true);
                          } else {
                            setFieldValue("ConversationTimeout", 180);
                            setFieldValue("ExpirationStepName", null);
                            setFieldValue("enableExpiration", false);
                          }
                        }}
                      />
                    }
                    label="Ativar"
                  />
                </MDBox>

                {formValues.enableExpiration && (
                  <MDBox component="form" pb={3} px={3}>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          variant="standard"
                          name="ConversationTimeout"
                          label={messages.conversationTimeoutLabel}
                          value={formValues.ConversationTimeout ?? ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value;
                            const onlyNumbers = value.replace(/[^0-9]/g, "");

                            if (onlyNumbers === "") {
                              setFieldValue("ConversationTimeout", "");
                            } else {
                              const numValue = Number(onlyNumbers);
                              if (!Number.isNaN(numValue)) {
                                setFieldValue("ConversationTimeout", numValue);
                              }
                            }
                          }}
                          type="text"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          fullWidth
                          error={!!errors.ConversationTimeout}
                          helperText={errors.ConversationTimeout}
                          placeholder="180 (padrão)"
                          required
                        />
                        {!errors.ConversationTimeout && (
                          <FormHelperText
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: 0.5,
                              color: "text.secondary",
                            }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                            {messages.conversationTimeoutHint}
                          </FormHelperText>
                        )}
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Autocomplete
                          options={(workflow as any)?.workflowSteps?.map((s: any) => s.stepName) ?? []}
                          value={(formValues.ExpirationStepName as string) || ""}
                          onChange={(_, newValue) => {
                            setFieldValue("ExpirationStepName", newValue || null);
                          }}
                          onInputChange={(_, newInputValue) => {
                            setFieldValue("ExpirationStepName", newInputValue || null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="standard"
                              label={messages.expirationStepNameLabel}
                              fullWidth
                              error={!!errors.ExpirationStepName}
                              helperText={errors.ExpirationStepName}
                              required
                            />
                          )}
                          disableClearable={false}
                          freeSolo={true}
                        />
                      </Grid>
                    </Grid>
                  </MDBox>
                )}
              </Card>
            </Grid>

            {/* Reativação */}
            <Grid size={12} mt={2}>
              <Card>
                <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="h6" fontWeight={"bold"}>
                    {messages.reactivationSectionTitle}
                  </MDTypography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(formValues.enableReactivation)}
                        onChange={(_, checked) => {
                          if (checked) {
                            const curr = formValues.reactivationTimeout;
                            const originalValue = originalReactivationTimeoutRef.current;

                            if (originalValue !== null && originalValue > 0) {
                              setFieldValue("reactivationTimeout", originalValue);
                            } else if (
                              curr === undefined ||
                              curr === null ||
                              (typeof curr === "string" && curr === "") ||
                              (typeof curr === "number" && curr === 0)
                            ) {
                              setFieldValue("reactivationTimeout", 15);
                            } else {
                              const currNum = Number(curr);
                              if (!Number.isFinite(currNum) || currNum <= 0) {
                                setFieldValue("reactivationTimeout", 15);
                              }
                            }
                            setFieldValue("enableReactivation", true);
                          } else {
                            setFieldValue("enableReactivation", false);
                          }
                        }}
                      />
                    }
                    label="Ativar"
                  />
                </MDBox>

                {formValues.enableReactivation && (
                  <MDBox component="form" pb={3} px={3}>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          variant="standard"
                          name="reactivationTimeout"
                          label={messages.reactivationTimeoutLabel}
                          value={formValues.reactivationTimeout ?? ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value;
                            const onlyNumbers = value.replace(/[^0-9]/g, "");

                            if (onlyNumbers === "") {
                              setFieldValue("reactivationTimeout", "");
                            } else {
                              const numValue = Number(onlyNumbers);
                              if (!Number.isNaN(numValue)) {
                                setFieldValue("reactivationTimeout", numValue);
                              }
                            }
                          }}
                          type="text"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          fullWidth
                          error={!!errors.reactivationTimeout}
                          helperText={errors.reactivationTimeout}
                          placeholder="15 (padrão)"
                          required
                        />
                        {!errors.reactivationTimeout && (
                          <FormHelperText
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: 0.5,
                              color: "text.secondary",
                            }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                            {messages.reactivationTimeoutHint}
                          </FormHelperText>
                        )}
                      </Grid>

                      <Grid size={12}>
                        <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 0.5 }}>
                          {messages.reactivationMessageLabel}
                        </MDTypography>
                        <TextField
                          variant="standard"
                          name="reactivationMessage"
                          value={reactivationMessage}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value;
                            if (value.length <= 1000) {
                              setFieldValue("reactivationMessage", value);
                            }
                          }}
                          multiline
                          rows={4}
                          fullWidth
                          error={!!errors.reactivationMessage}
                          helperText={errors.reactivationMessage}
                          required
                          inputProps={{
                            maxLength: 1000,
                          }}
                        />
                        <MDTypography
                          variant="caption"
                          color={messageLength > 1000 ? "error" : "text"}
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {messageLength} / 1000
                        </MDTypography>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          variant="standard"
                          name="reactivationMaxMessages"
                          label={messages.reactivationMaxMessagesLabel}
                          value={formValues.reactivationMaxMessages ?? ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value;
                            const onlyNumbers = value.replace(/[^0-9]/g, "");

                            if (onlyNumbers === "") {
                              setFieldValue("reactivationMaxMessages", "");
                            } else {
                              const numValue = Number(onlyNumbers);
                              if (!Number.isNaN(numValue)) {
                                setFieldValue("reactivationMaxMessages", numValue);
                              }
                            }
                          }}
                          type="text"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          fullWidth
                          error={!!errors.reactivationMaxMessages}
                          helperText={errors.reactivationMaxMessages}
                          placeholder="5 (padrão)"
                        />
                        {!errors.reactivationMaxMessages && (
                          <FormHelperText
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: 0.5,
                              color: "text.secondary",
                            }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                            {messages.reactivationMaxMessagesHint}
                          </FormHelperText>
                        )}
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          variant="standard"
                          name="reactivationInterval"
                          label={messages.reactivationIntervalLabel}
                          value={formValues.reactivationInterval ?? ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value;
                            const onlyNumbers = value.replace(/[^0-9]/g, "");

                            if (onlyNumbers === "") {
                              setFieldValue("reactivationInterval", "");
                            } else {
                              const numValue = Number(onlyNumbers);
                              if (!Number.isNaN(numValue)) {
                                setFieldValue("reactivationInterval", numValue);
                              }
                            }
                          }}
                          type="text"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          fullWidth
                          error={!!errors.reactivationInterval}
                          helperText={errors.reactivationInterval}
                          placeholder="60 (padrão)"
                        />
                        {!errors.reactivationInterval && (
                          <FormHelperText
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: 0.5,
                              color: "text.secondary",
                            }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                            {messages.reactivationIntervalHint}
                          </FormHelperText>
                        )}
                      </Grid>
                    </Grid>
                  </MDBox>
                )}
              </Card>
            </Grid>

            <Grid size={12} mt={2}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="bold" mb={2}>
                    {messages.workflowKeyWordsSectionTitle}
                  </MDTypography>
                  <WorkflowKeyWordsSection
                    messages={messages}
                    workflowSteps={(workflow as IWorkflow)?.workflowSteps || []}
                  />
                </MDBox>
              </Card>
            </Grid>

            <MDBox mt={2} display="flex" justifyContent="flex-end" gap={2}>
              <MDButton
                variant="gradient"
                color="light"
                onClick={() => router(ROUTE_URLS.workflow(lang))}
                isLoading={isLoading}
              >
                {messages.cancelButton}
              </MDButton>

              <MDButton variant="gradient" color="dark" onClick={submitForm} isLoading={isLoading}>
                {isLoading ? loadingIcon : messages.updateButton}
              </MDButton>
            </MDBox>
          </MDBox>
        );
      }}
    </Formik>
    <ProductSearchAssertivenessModal
      isOpen={isAssertivenessModalOpen}
      onClose={closeAssertivenessModal}
      messages={messages}
    />
  </>
  );
}
